import { bookActions } from '@/actions';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { APP_ROUTES } from '@/constants/pages';
import { checkoutServices, PaymentResultStatus } from '@/features/checkout/checkout.services';
import { promiseWrapper } from '@/helpers';
import { saveBookingTrackingProps } from '@/helpers/checkout';
import { bookingFlowTrackingSession, mapSelectedPaymentMethod } from '@/helpers/checkoutFlowTrackingSession';
import { getEmbedContext } from '@/helpers/embed';
import { PaymentResultMessage, postPaymentResult } from '@/helpers/embedPayment';
import { fetchPlace, getEmployee, getServices } from '@/helpers/placeHelperTS';
import { _s } from '@/locale';
import {
  BokadirektPaySuccessRedirectData,
  BookingCheckoutState,
  readAndClearSavedCheckoutState,
  SubmitBookingContext,
} from '@/pages/booking/checkout/BookingCheckout.hooks';
import { keepAliveNotificationSchema } from '@/types/api/services/swish';
import { SelectedPaymentMethod } from '@/types/checkout';
import { bookDateSchema, bookStateSchema } from '@/types/state/book';
import * as Sentry from '@sentry/react';
import { Buffer } from 'buffer';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { paymentsServices } from '../../payments.services';
import { getBokadirektPayErrorFallbackPath } from '../../utils/paymentUrls';

export const baseTranslationKey = 'pages.validate-bokadirekt-pay.ValidateBokadirektPay';

/**
 * Error codes from BokadirektPay backend.
 * Prefixed with PAYMENT_ for payment-related errors
 * Prefixed with BOOKING_ for booking-related errors
 */
type BokadirektPayErrorCode =
  // Payment errors
  | 'PAYMENT_CANCELLED'
  | 'PAYMENT_DECLINED'
  | 'PAYMENT_AUTH_FAILED'
  | 'PAYMENT_3DS_FAILED'
  | 'PAYMENT_CARD_BLOCKED'
  | 'PAYMENT_CARD_EXPIRED'
  | 'PAYMENT_INSUFFICIENT_FUNDS'
  | 'PAYMENT_FRAUD_SUSPECTED'
  | 'PAYMENT_ISSUER_UNAVAILABLE'
  | 'PAYMENT_GENERIC_ERROR'
  // Booking errors
  | 'BOOKING_TIME_UNAVAILABLE'
  | 'BOOKING_EMPLOYEE_NOT_FOUND'
  | 'BOOKING_PLACE_NOT_FOUND'
  | 'BOOKING_FAILED'
  // Server/unknown errors
  | 'SERVER_ERROR'
  | 'UNKNOWN_ERROR';

type CheckoutError = {
  status: number;
  message: string;
  errorCode?: BokadirektPayErrorCode;
  clientError?: string;
  /** Backend-provided redirect URL for error recovery */
  redirectUrl?: string;
};

const SERVER_ERROR: CheckoutError = { message: 'Could not get status of the payment', status: 500 };

type SubmitBokadirektPayContext = Omit<SubmitBookingContext, 'successCallback' | 'errorCallback'> & {
  savedCheckoutState: BookingCheckoutState;
  eventSource: EventSource;
  resource_id: number;
  checkoutRef: string;
  sessionId: string;
  sessionResult: string;
  successCallback: (data: BokadirektPaySuccessRedirectData) => void;
  errorCallback: (error: CheckoutError) => void;
  redirectResult?: string;
};

export type SavedBookingCheckoutState = Omit<
  SubmitBokadirektPayContext,
  'bookState' | 'successCallback' | 'errorCallback' | 'eventSource' | 'resource_id'
>;

type BokadirektPaySuccessRedirect = {
  pathname: '/booking/confirmation' | '/bundles/confirmation';
  search?: string;
  state?: Record<string, unknown>;
};

/** What the pay popup shows when the frame that opened it is gone and the report could not be delivered. */
export type PopupTerminal = { kind: 'success' } | { kind: 'error'; message?: string };

type BokadirektPayErrorRedirect = {
  pathname: string;
  search?: string;
  state: {
    savedCheckoutState?: BookingCheckoutState;
    selectedPaymentMethod?: SelectedPaymentMethod;
    /** Read by the weekpicker, which renders its "time taken" message off it. */
    retry?: boolean;
  };
};

/**
 * If we are redirected from bokadirekt pay we need to check if the payment and booking was successful or not and redirect
 * the user accordingly. The actual booking has already been submitted and will be completed or cancelled
 * in the backend.
 */
async function handleValidateBokadirektPay(
  context: SubmitBokadirektPayContext & { resourceType?: 'booking' | 'bundle' },
) {
  const {
    bookingTrackingProps,
    eventSource,
    resource_id,
    checkoutRef,
    redirectResult,
    resourceType = 'booking',
  } = context;

  // Phase 1: If redirectResult is present, submit it to Adyen first
  if (redirectResult) {
    try {
      const paymentResult = await checkoutServices.submitCheckoutDetails({
        checkoutRef,
        resourceType,
        redirectResult,
      });

      if (paymentResult.status === PaymentResultStatus.FAILED) {
        const error: CheckoutError = {
          status: 400,
          message: 'Payment failed',
          errorCode: paymentResult.errorCode as BokadirektPayErrorCode,
          clientError: paymentResult.errorMessage || _s(`${baseTranslationKey}.failedBookingError`),
          redirectUrl: paymentResult.redirectUrl,
        };
        context.errorCallback(error);
        eventSource.close();
        return;
      }
    } catch (error) {
      // Fall through to SSE — it will deliver the result
      console.error('[handleValidateBokadirektPay] Error submitting checkout details:', error);
    }
  }

  // Phase 2: Listen for SSE events (timeout is scheduled by SSE endpoint on connect)
  if (eventSource) {
    eventSource.onmessage = (e) => {
      let bokadirektPayNotification: any;
      try {
        bokadirektPayNotification = JSON.parse(e.data);
      } catch {
        console.error('[handleValidateBokadirektPay] Failed to parse SSE message:', e.data);
        return;
      }
      const validatedKeepAliveNotification = keepAliveNotificationSchema.safeParse(bokadirektPayNotification);

      if (validatedKeepAliveNotification.success === true && validatedKeepAliveNotification.data.keepAlive) {
        return;
      }

      // Closed before the callbacks: the pay popup's callbacks call window.close(), and
      // Chromium fires `error` on a still-open stream while the window closes, which
      // would report a failure after the outcome was already posted to the host.
      eventSource.close();

      if (bokadirektPayNotification.status === 'failed') {
        const error: CheckoutError = {
          status: 400,
          message: 'The bokadirekt payment was not successful',
          errorCode: bokadirektPayNotification.errorCode as BokadirektPayErrorCode,
          clientError: bokadirektPayNotification.clientError || _s(`${baseTranslationKey}.failedBookingError`),
          redirectUrl: bokadirektPayNotification.redirectUrl,
        };
        context.errorCallback(error);
        return;
      } else {
        if (bookingTrackingProps) saveBookingTrackingProps(bookingTrackingProps);
        // For bundles, fetch the BookingBundleId from the checkout status endpoint
        // (the SSE event signals success but doesn't carry the id; for bookings the
        // resource_id from the URL is already the booking id).
        if (resourceType === 'bundle') {
          checkoutServices
            .getCheckoutStatus({ checkoutRef, resourceType: 'bundle' })
            .then((status) => {
              const bundleId = status?.productData?.bookingBundleId ?? status?.id ?? resource_id;
              context.successCallback({ resourceId: bundleId, resourceType: 'bundle' });
            })
            .catch(() => context.errorCallback(SERVER_ERROR));
        } else {
          context.successCallback({ resourceId: resource_id, resourceType: 'booking' });
        }
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
      context.errorCallback(SERVER_ERROR);
    };
  }
}

const timeBookStateRequestSchema = bookDateSchema.extend({
  employees: z.array(z.number()),
});

const bookStateRequestSchema = z.object({
  employee: bookStateSchema.shape.employee,
  services: z.array(z.number()),
  time: timeBookStateRequestSchema,
  place: z.object({
    id: z.number(),
    slug: z.string(),
  }),
  campaignsIds: z.array(z.number()).optional(),
  dealId: z.number().optional(),
  isSistaminuten: z.boolean().optional(),
});

const useValidateBokadirektPay = () => {
  const [checkoutState] = useState<SavedBookingCheckoutState | null>(() =>
    readAndClearSavedCheckoutState<SavedBookingCheckoutState>(),
  );
  const [redirect, setRedirect] = useState<BokadirektPaySuccessRedirect | BokadirektPayErrorRedirect | false>(false);
  const [terminal, setTerminal] = useState<PopupTerminal | false>(false);
  // One report per popup: the frame treats the first as final, and nothing after it
  // (the stream erroring as the window closes) may read as a second outcome.
  const reportedRef = useRef(false);
  const location = useLocation();
  const { type: resourceType } = useParams<{ type?: 'booking' | 'bundle' }>();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const queryData = searchParams.get('data');
  const bookStateRequestData = (() => {
    if (!queryData) return null;
    const base64 = queryData.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    try {
      return JSON.parse(Buffer.from(padded, 'base64').toString());
    } catch {
      return null;
    }
  })();
  const { bookingRequestBody, bookingTrackingProps, savedCheckoutState } = checkoutState || {};
  const resource_id = Number(searchParams.get('resourceId'));
  const checkoutRef = searchParams.get('checkoutRef');
  const sessionId = searchParams.get('sessionId');
  const sessionResult = searchParams.get('sessionResult');
  const redirectResult = searchParams.get('redirectResult');

  useEffect(() => {
    if (bookStateRequestData) {
      const validatedStateData = bookStateRequestSchema.safeParse(bookStateRequestData);
      if (validatedStateData.success === false) {
        Sentry.captureException(validatedStateData.error);
      }
    }

    const controller = new AbortController();
    const { signal } = controller;

    const restoreBookState = async () => {
      if (!bookStateRequestData) return;
      const { place, services, time, employee, campaignsIds, dealId, isSistaminuten } = bookStateRequestData;
      const { data, error } = await promiseWrapper(
        fetchPlace({ abortSignal: signal, id: place.id, slug: place.slug, dealId, isSistaminuten }),
      );
      const fetchedPlace = data;

      if (error || !fetchedPlace?.id || !fetchedPlace?.about?.slug) {
        Sentry.captureMessage('Place could not be fetched');
        return;
      }

      dispatch(bookActions.addPlace(fetchedPlace));

      const rebuiltServices = getServices({ serviceIds: services, place: fetchedPlace });
      const { employees, ...restOfTime } = time;
      const rebuiltTimeEmployee = getEmployee({ employeeId: employees[0], place: fetchedPlace });
      const rebuiltTime = { ...restOfTime, employees: [rebuiltTimeEmployee] };
      const requestedIds = new Set(campaignsIds ?? []);
      const rebuiltCampaigns = (fetchedPlace.campaigns ?? []).filter(
        (c: { id?: number | null }) => typeof c.id === 'number' && requestedIds.has(c.id),
      );

      dispatch(
        bookActions.restoreBookState({
          employee,
          services: rebuiltServices,
          time: rebuiltTime,
          campaigns: rebuiltCampaigns,
        }),
      );
    };

    // Pinned to the frame that opened the popup, which routes on it; with no opener
    // left, the visitor is told here instead and closes the window themselves.
    const reportToOpener = (message: PaymentResultMessage, fallback: PopupTerminal) => {
      if (reportedRef.current) return;
      reportedRef.current = true;
      if (postPaymentResult(message)) {
        window.close();
        return;
      }
      setTerminal(fallback);
    };

    const successCallback = ({ resourceId, resourceType }: BokadirektPaySuccessRedirectData) => {
      const method = mapSelectedPaymentMethod(savedCheckoutState?.selectedPaymentMethod?.type);
      if (method) bookingFlowTrackingSession.checkoutPaymentMethod('success', method, { flush: true });

      if (getEmbedContext() === 'paymentPopup') {
        reportToOpener(
          { type: 'BD_WIDGET_PAYMENT_COMPLETE', resourceId: String(resourceId), resourceType },
          { kind: 'success' },
        );
        return;
      }

      if (resourceType === 'bundle') {
        setRedirect({
          pathname: '/bundles/confirmation',
          search: `?bundleId=${resourceId}`,
        });
        return;
      }

      const savedCheckoutPaymentMethod = savedCheckoutState?.selectedPaymentMethod;
      const selectedPaymentMethod =
        savedCheckoutPaymentMethod?.type === CHECKOUT_PAYMENT_METHOD.STORED_COF
          ? {
              type: savedCheckoutPaymentMethod.type,
              id: savedCheckoutPaymentMethod.id,
              brand: savedCheckoutPaymentMethod.brand,
              lastFour: savedCheckoutPaymentMethod.lastFour,
              onlineFee: savedCheckoutPaymentMethod.onlineFee,
            }
          : savedCheckoutPaymentMethod;

      setRedirect({
        pathname: '/booking/confirmation',
        search: `?bookingId=${resourceId}&type=bokadirektPay`,
        state: { ...(selectedPaymentMethod && { selectedPaymentMethod }) },
      });
    };

    const errorCallback = async (error: CheckoutError) => {
      const method = mapSelectedPaymentMethod(savedCheckoutState?.selectedPaymentMethod?.type);
      if (method) bookingFlowTrackingSession.checkoutPaymentMethod('error', method);

      if (getEmbedContext() === 'paymentPopup') {
        reportToOpener(
          { type: 'BD_WIDGET_PAYMENT_ERROR', errorCode: error.errorCode, errorMessage: error.clientError },
          { kind: 'error', message: error.clientError },
        );
        return;
      }

      const { status, clientError, errorCode, redirectUrl } = error;

      // The saved checkout state was read and cleared on mount, so a return to
      // checkout has to carry it or the visitor's checkout comes back empty.
      const redirectTo = (
        pathname: string,
        state: BokadirektPayErrorRedirect['state'] = {},
      ): BokadirektPayErrorRedirect => ({
        pathname,
        search: '',
        state: pathname === '/booking/checkout' ? { savedCheckoutState } : state,
      });

      // Server errors (status > 500) - show generic error and leave the flow
      if (status > 500) {
        toast(({ closeToast }) => (
          <Snackbar label={_s(`${baseTranslationKey}.unknownError`)} type="danger" onClose={closeToast} />
        ));
        setRedirect(redirectTo(getBokadirektPayErrorFallbackPath()));
        return;
      }

      // Restore book state from URL data param so Redux is populated when returning to checkout
      await restoreBookState();

      // Show the error message to the user
      const errorMessage = clientError || _s(`${baseTranslationKey}.failedBookingError`);
      toast(({ closeToast }) => <Snackbar label={errorMessage} type="danger" onClose={closeToast} />);

      // Use backend-provided redirectUrl if available, otherwise the bundle's fallback
      if (redirectUrl) {
        // The weekpicker renders its own "time taken" message off `retry`, the same signal
        // checkout sends it on a 409. The snackbar above auto-closes, so without it the
        // visitor is back on the calendar with no account of why. Gated on the one code
        // that message is true for: every BOOKING_* code redirects here, but an employee
        // or ERP failure leaves the slot free, and claiming it was taken would be a lie.
        const slotWasTaken =
          errorCode === 'BOOKING_TIME_UNAVAILABLE' &&
          matchPath(redirectUrl, { path: APP_ROUTES.BOOKING_CALENDAR }) != null;
        setRedirect(redirectTo(redirectUrl, slotWasTaken ? { retry: true } : {}));
      } else {
        setRedirect(redirectTo(getBokadirektPayErrorFallbackPath()));
      }
    };

    if (!checkoutRef) {
      errorCallback(SERVER_ERROR);
      return;
    }

    const context: SubmitBokadirektPayContext & { resourceType?: 'booking' | 'bundle' } = {
      bookingRequestBody,
      bookingTrackingProps,
      resource_id: resource_id,
      checkoutRef: checkoutRef,
      sessionId,
      sessionResult,
      savedCheckoutState,
      redirectResult,
      resourceType: resourceType ?? 'booking',
      abortCallback: () => {},
      errorCallback,
      successCallback,
      eventSource: paymentsServices.waitForBokadirektPayNotification(checkoutRef),
    };

    handleValidateBokadirektPay(context);

    return () => {
      controller.abort();
      context.eventSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { redirect, terminal };
};

export default useValidateBokadirektPay;
